// @wc-ignore-file
import { supabase } from "@/lib/supabase";
import type { Database } from "@shared/types/supabase.types";

type Functions = Database["public"]["Functions"];

/** CR-203 D2: one row of list_workspace_projects — every project of the workspace. */
export type WorkspaceProject = Functions["list_workspace_projects"]["Returns"][number];
export type ProjectJoinRequestResult = Functions["request_project_join"]["Returns"][number];
/** CR-203 S3: one pending request as a project owner sees it (name/email included). */
export type ProjectJoinRequest = Functions["list_project_join_requests"]["Returns"][number];

/**
 * CR-203: every project of a workspace the caller belongs to, with the
 * caller's role (null when not a member) and their latest join request.
 * Raises "Workspace not found or not accessible" for an outsider.
 */
export async function listWorkspaceProjects(workspaceId: string): Promise<WorkspaceProject[]> {
  const { data, error } = await supabase.rpc("list_workspace_projects", {
    p_workspace_id: workspaceId,
  });
  if (error) throw new Error(error.message);
  return data ?? [];
}

/** CR-203 D4: a workspace owner/admin joins a project as project admin (idempotent). */
export async function joinWorkspaceProject(projectId: string): Promise<void> {
  const { error } = await supabase.rpc("join_workspace_project", { p_project_id: projectId });
  if (error) throw new Error(error.message);
}

/** CR-203 D3: a workspace member asks to join (idempotent while pending). */
export async function requestProjectJoin(projectId: string): Promise<ProjectJoinRequestResult> {
  const { data, error } = await supabase.rpc("request_project_join", {
    p_project_id: projectId,
  });
  if (error) throw new Error(error.message);
  const row = data?.[0];
  if (!row) throw new Error("request_project_join returned no row");
  return row;
}

/**
 * CR-203 S3: the project's pending join requests. Project OWNERS only — the
 * RPC raises "Project not found or not accessible" for anyone else, so callers
 * gate on the owner role before enabling the query.
 */
export async function listProjectJoinRequests(projectId: string): Promise<ProjectJoinRequest[]> {
  const { data, error } = await supabase.rpc("list_project_join_requests", {
    p_project_id: projectId,
    p_status: "pending",
  });
  if (error) throw new Error(error.message);
  return data ?? [];
}

/** CR-203 D6: an owner approves a pending request; the requester becomes a `member`. */
export async function approveProjectJoinRequest(requestId: string): Promise<void> {
  const { error } = await supabase.rpc("approve_project_join_request", {
    p_request_id: requestId,
  });
  if (error) throw new Error(error.message);
}

/** CR-203: an owner declines a pending request (the requester may ask again, Q3). */
export async function rejectProjectJoinRequest(requestId: string): Promise<void> {
  const { error } = await supabase.rpc("reject_project_join_request", {
    p_request_id: requestId,
  });
  if (error) throw new Error(error.message);
}
